<script setup lang="ts">
import { computed } from 'vue'
import { normalizeDropdownMenuItems, type DropdownItem } from './dropdownMenuItems'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/shared/components/ui'
import { ChevronRight, Loader2 } from 'lucide-vue-next'

export type { DropdownItem }

const props = defineProps<{
  items: DropdownItem[]
  contentClass?: string
  width?: string
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  sideOffset?: number
}>()

const displayItems = computed(() => normalizeDropdownMenuItems(props.items))

const emit = defineEmits<{
  (e: 'select', item: DropdownItem): void
}>()

const handleItemClick = (item: DropdownItem) => {
  if (item.disabled || item.loading) return
  if (item.action) {
    item.action()
  }
  emit('select', item)
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <slot name="trigger"></slot>
    </DropdownMenuTrigger>

    <DropdownMenuContent
      :side="side"
      :align="align || 'end'"
      :class="[width || 'w-64', 'p-0 overflow-hidden', contentClass]"
      :side-offset="sideOffset || 8"
    >
      <div v-if="$slots.header">
        <slot name="header"></slot>
        <DropdownMenuSeparator class="m-0" />
      </div>

      <div class="py-1.5 px-1">
        <template v-for="(item, index) in displayItems" :key="index">
          <DropdownMenuSeparator v-if="item.type === 'separator'" class="-mx-1 my-1.5" />

          <DropdownMenuItem
            v-else
            :disabled="item.disabled || item.loading"
            @click="handleItemClick(item)"
            :class="[
              'group flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-all duration-200 rounded-md',
              item.danger
                ? 'text-destructive focus:bg-destructive/10 focus:text-destructive'
                : 'text-foreground focus:bg-accent focus:text-accent-foreground'
            ]"
          >
            <div
              v-if="item.icon || item.loading"
              :class="[
                'flex items-center justify-center transition-colors',
                item.danger
                  ? 'text-destructive'
                  : 'text-muted-foreground group-hover:text-foreground'
              ]"
            >
              <Loader2 v-if="item.loading" class="h-5 w-5 animate-spin" />
              <component v-else :is="item.icon" class="h-5 w-5" />
            </div>
            <span class="flex-1 font-medium text-[13.5px]">{{ item.label }}</span>
            <ChevronRight
              v-if="!item.danger"
              class="h-4 w-4 text-muted-foreground opacity-0 -translate-x-1 transition-all group-hover:opacity-100 group-hover:translate-x-0"
            />
          </DropdownMenuItem>
        </template>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
